import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';

import { beEditFee } from 'actions/taxes/beEditFee.action';
import { beEditTax } from 'actions/taxes/beEditTax.action';
import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { usePrepareWriteOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';

import { AddRuleModal } from './AddRuleModal';
import { Card } from './Card';
import { DeleteRuleButton } from './DeleteRuleButton';

import CN from './RuleCard.module.scss';

export const RuleCard = ({ rule, variant }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { val: isRuleModalOpen, yay: showAddRuleModal, nay: hideAddRuleModal } = useTip(false);

  const { data: editTaxData, up: editTaxMutation, isLoading: isEditingTax, isError: isEditingTaxError } = usePrepareWriteOnly(beEditTax);
  const { data: editFeeData, up: editFeeMutation, isLoading: isEditingFee, isError: isEditingFeeError } = usePrepareWriteOnly(beEditFee);

  const hasFeeError = editFeeData?.json?.errors || editFeeData?.is?.ok === false || isEditingFeeError;
  const hasTaxError = editTaxData?.json?.errors || editTaxData?.is?.ok === false || isEditingTaxError;
  const hasError = hasTaxError || hasFeeError;

  const queryClient = useQueryClient();

  const variantMap = {
    tax: {
      name: t('staffAndBusiness.taxesAndFees.taxBadge'),
      badgeVariant: 'sky',
    },
    fee: {
      name: t('staffAndBusiness.taxesAndFees.feeBadge'),
      badgeVariant: 'fine',
    },
  };

  const appliesToMap = {
    products: t('staffAndBusiness.taxesAndFees.productsLabel'),
    services: t('staffAndBusiness.taxesAndFees.servicesLabel'),
    products_and_services: t('staffAndBusiness.taxesAndFees.productsAndServicesLabel'),
  };

  const editRule = async (newRule) => {
    const isTax = variant === 'tax';

    if (isTax) {
      const tax = {
        name: newRule.name,
        percentage: newRule.amount,
      };
      const response = await editTaxMutation({ id: rule.id, data: tax });
      if (response?.is?.ok) {
        queryClient.invalidateQueries({ queryKey: ['beGetSalonTaxesAndFees'] });
        hideAddRuleModal();
      }
    } else {
      const fee = {
        name: newRule.name,
        amount: newRule.isAmount ? newRule.amount : null,
        percentage: newRule.isAmount ? null : newRule.amount,
      };
      const response = await editFeeMutation({ id: rule.id, data: fee });
      if (response?.is?.ok) {
        queryClient.invalidateQueries({ queryKey: ['beGetSalonTaxesAndFees'] });
        hideAddRuleModal();
      }
    }
  };

  return (
    <>
      {isRuleModalOpen && <AddRuleModal rule={rule} isOpen={isRuleModalOpen} onClose={hideAddRuleModal} onConfirm={editRule} isDisabled={isEditingTax || isEditingFee} hasError={hasError} />}
      <Card data-bem="RuleCard" variant="tax" className={cn(CN.component, isDarkMode && CN.dark)}>
        <div className={CN.cardHeader}>
          <div className={CN.ruleNameContainer}>
            <span className={CN.ruleNameText}>{rule.name}</span>
          </div>
          <div className={CN.ruleActionsContainer}>
            <Button variant="secondary" onClick={showAddRuleModal} tag="RuleCard__editRuleButton" width="small">
              {t('staffAndBusiness.taxesAndFees.editButton')}
            </Button>
            <DeleteRuleButton rule={rule} variant={variant} />
          </div>
        </div>

        <div className={CN.ruleBody}>
          <div className={CN.ruleInfoContainer}>
            <span className={CN.ruleInfoLabel}>{t('staffAndBusiness.taxesAndFees.amountLabel')}</span>
            {fromAnyToBool(rule.amount) && (
              <span className={CN.ruleInfoText}>
                <CurrencySign value={rule.amount} />
              </span>
            )}
            <div className={CN.row}>
              {fromAnyToBool(rule.percentage) && <span className={CN.ruleInfoText}>{rule.percentage}%</span>}
              <Badge variant={variantMap[variant].badgeVariant} height="sm">
                {variantMap[variant].name}
              </Badge>
            </div>
          </div>

          <div className={CN.ruleInfoContainer}>
            <span className={CN.ruleInfoLabel}>{t('staffAndBusiness.taxesAndFees.appliesToLabel')}</span>
            <div className={CN.row}>
              <span
                className={cn({
                  [CN.ruleInfoText]: true,
                  [CN.onlyText]: true,
                })}
              >
                {appliesToMap[rule.applied_to]}
              </span>
            </div>
          </div>
          <div className={CN.ruleInfoContainer}>
            <span className={CN.ruleInfoLabel}>{t('staffAndBusiness.taxesAndFees.autoApplyLabel')}</span>
            <div className={CN.row}>
              <div className={CN.badge}>
                {rule.is_default ? (
                  <Badge height="md" variant="success">
                    <span className={CN.autoApplyText}>{t('staffAndBusiness.taxesAndFees.autoApplyBadge')}</span>
                  </Badge>
                ) : (
                  <Badge height="md" variant="sky">
                    <span className={CN.autoApplyText}>{t('staffAndBusiness.taxesAndFees.noneApplyBadge')}</span>
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};
